import { computed, ref } from 'vue'
import type { SeekerConversation } from '../types'
import { getAiUserId, useAiClient, useAiHost } from '../setup'

export type HistoryGroup = {
  key: string
  label: string
  items: SeekerConversation[]
}

const activeSceneId = ref('mes-general')
const chatResetKey = ref(0)
const activeSessionId = ref<string | null>(null)
const conversations = ref<SeekerConversation[]>([])
const historyLoading = ref(false)
const historyError = ref('')
const historyMoreId = ref<string | null>(null)
const renamingId = ref<string | null>(null)
const renameDraft = ref('')
/** When set, ChatPage should open this session then enter share-select mode. */
const shareIntentSessionId = ref<string | null>(null)

type NewChatHandler = () => void | Promise<void>
let newChatHandler: NewChatHandler | null = null
/** Set when reset was requested before ChatPage could handle it. */
let pendingNewChat = false

/** ChatPage registers so「新建对话」can reset even if keep-alive watch misses. */
export function registerNewChatHandler(handler: NewChatHandler): () => void {
  newChatHandler = handler
  return () => {
    if (newChatHandler === handler) newChatHandler = null
  }
}

/** ChatPage drains this on bootstrap / nav reset. */
export function consumePendingNewChat(): boolean {
  if (!pendingNewChat) return false
  pendingNewChat = false
  return true
}

function requestNewChat(): void {
  activeSessionId.value = null
  shareIntentSessionId.value = null
  pendingNewChat = true
  chatResetKey.value += 1
  void newChatHandler?.()
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function historyGroupLabel(updatedAt: string, now = new Date()): string {
  const date = new Date(updatedAt)
  if (Number.isNaN(date.getTime())) return '更早'
  const day = startOfLocalDay(date)
  const today = startOfLocalDay(now)
  const diffDays = Math.floor((today.getTime() - day.getTime()) / 86_400_000)
  if (diffDays <= 0) return '今天'
  if (diffDays < 7) return '7 天内'
  if (diffDays < 30) return '30 天内'
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

export function useAiChatNav() {
  const externalUserId = computed(() => getAiUserId())

  const conversationGroups = computed((): HistoryGroup[] => {
    const now = new Date()
    const rank = (label: string): number => {
      if (label === '今天') return 0
      if (label === '7 天内') return 1
      if (label === '30 天内') return 2
      if (label === '更早') return Number.MAX_SAFE_INTEGER
      const m = /^(\d{4})-(\d{2})$/.exec(label)
      if (m) return 100 + (3000 * 12 - (Number(m[1]) * 12 + Number(m[2])))
      return 9999
    }

    const byUpdatedDesc = (a: SeekerConversation, b: SeekerConversation): number =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()

    const pinned = conversations.value.filter((c) => c.pinned).sort(byUpdatedDesc)
    const buckets = new Map<string, SeekerConversation[]>()

    for (const item of conversations.value) {
      if (item.pinned) continue
      const label = historyGroupLabel(item.updated_at, now)
      const list = buckets.get(label)
      if (list) list.push(item)
      else buckets.set(label, [item])
    }

    for (const list of buckets.values()) list.sort(byUpdatedDesc)

    const groups: HistoryGroup[] = []
    if (pinned.length) groups.push({ key: 'pinned', label: '置顶', items: pinned })
    const labels = [...buckets.keys()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    for (const label of labels) {
      groups.push({ key: label, label, items: buckets.get(label) || [] })
    }
    return groups
  })

  function selectScene(id: string): void {
    if (activeSceneId.value === id) return
    activeSceneId.value = id
    requestNewChat()
  }

  function createNewChat(): void {
    requestNewChat()
  }

  function openConversation(id: string): void {
    if (activeSessionId.value === id) {
      activeSessionId.value = null
      void Promise.resolve().then(() => {
        activeSessionId.value = id
      })
      return
    }
    activeSessionId.value = id
  }

  function setActiveSessionId(id: string | null): void {
    activeSessionId.value = id
  }

  async function refreshConversations(): Promise<void> {
    const client = useAiClient()
    historyLoading.value = true
    historyError.value = ''
    try {
      conversations.value = await client.listConversations(externalUserId.value, 80)
    } catch (err) {
      historyError.value = err instanceof Error ? err.message : String(err)
    } finally {
      historyLoading.value = false
    }
  }

  function toggleHistoryMore(id: string, event?: Event): void {
    event?.stopPropagation()
    historyMoreId.value = historyMoreId.value === id ? null : id
  }

  function closeHistoryMore(): void {
    historyMoreId.value = null
  }

  async function togglePin(item: SeekerConversation, event?: Event): Promise<void> {
    event?.stopPropagation()
    historyMoreId.value = null
    const client = useAiClient()
    try {
      const updated = await client.updateConversation(item.id, {
        pinned: !item.pinned,
        externalUserId: externalUserId.value,
      })
      conversations.value = conversations.value
        .map((c) => (c.id === updated.id ? updated : c))
        .sort((a, b) => {
          if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        })
    } catch (err) {
      historyError.value = err instanceof Error ? err.message : String(err)
    }
  }

  function beginRename(item: SeekerConversation, event?: Event): void {
    event?.stopPropagation()
    historyMoreId.value = null
    renamingId.value = item.id
    renameDraft.value = item.title
  }

  function cancelRename(): void {
    renamingId.value = null
    renameDraft.value = ''
  }

  async function commitRename(item: SeekerConversation): Promise<void> {
    if (renamingId.value !== item.id) return
    const titleText = renameDraft.value.trim()
    renamingId.value = null
    renameDraft.value = ''
    if (!titleText || titleText === item.title) return
    const client = useAiClient()
    try {
      const updated = await client.updateConversation(item.id, {
        title: titleText,
        externalUserId: externalUserId.value,
      })
      conversations.value = conversations.value.map((c) => (c.id === updated.id ? updated : c))
    } catch (err) {
      historyError.value = err instanceof Error ? err.message : String(err)
    }
  }

  async function removeConversation(item: SeekerConversation, event?: Event): Promise<boolean> {
    event?.stopPropagation()
    historyMoreId.value = null
    const host = useAiHost()
    const confirmed = host.confirm
      ? host.confirm(`删除对话「${item.title}」？`)
      : window.confirm(`删除对话「${item.title}」？`)
    if (!confirmed) return false
    const client = useAiClient()
    try {
      await client.deleteConversation(item.id, externalUserId.value)
      conversations.value = conversations.value.filter((c) => c.id !== item.id)
      if (activeSessionId.value === item.id) {
        createNewChat()
      }
      return true
    } catch (err) {
      historyError.value = err instanceof Error ? err.message : String(err)
      return false
    }
  }

  function requestShareConversation(item: SeekerConversation, event?: Event): void {
    event?.stopPropagation()
    historyMoreId.value = null
    shareIntentSessionId.value = item.id
    openConversation(item.id)
  }

  function clearShareIntent(): void {
    shareIntentSessionId.value = null
  }

  return {
    activeSceneId,
    chatResetKey,
    activeSessionId,
    conversations,
    historyLoading,
    historyError,
    historyMoreId,
    renamingId,
    renameDraft,
    shareIntentSessionId,
    externalUserId,
    conversationGroups,
    selectScene,
    createNewChat,
    openConversation,
    setActiveSessionId,
    refreshConversations,
    toggleHistoryMore,
    closeHistoryMore,
    togglePin,
    beginRename,
    cancelRename,
    commitRename,
    removeConversation,
    requestShareConversation,
    clearShareIntent,
  }
}
